import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { AppGlobals } from '../app.global';
import { first } from 'rxjs/operators';
import { ConnectionService } from 'ng-connection-service';
import { MessageService } from 'primeng/api';
import { resolveApiBaseUrl } from '../_helpers/api-base-url';
@Injectable({
    providedIn: 'root',
})
export class HttpService {
    private headers = this._global.headers;
    public BASE_URL: string = environment.apiUrl;
    status = 'ONLINE';
    isConnected: any = true;
    error: any = '';

    /**
     * Picks the host for a request: an explicit baseUrl argument wins, then the per-service
     * override matching the URL's path prefix, then BASE_URL. See _helpers/api-base-url.ts.
     */
    private resolveBaseUrl(url: string, baseUrl?: string | null): string {
        return resolveApiBaseUrl(url, baseUrl);
    }

    constructor(
        private httpClient: HttpClient,
        private _global: AppGlobals,
        private connectionService: ConnectionService,
        public messageService: MessageService
    ) {
        this.connectionService.monitor().subscribe((isConnected) => {
            this.isConnected = isConnected;
            if (this.isConnected) {
                this.status = 'ONLINE';
            } else {
                this.status = 'OFFLINE';
                this.messageService.add({ key: 't2', severity: 'warn', summary: 'Warning', detail: 'Please check your Internet connection', life: 4000 });
            }
        });
    }
    get(url: string, params = {}, baseUrl: any = null) {
        let promise;
        baseUrl = this.resolveBaseUrl(url, baseUrl);
        promise = new Promise((resolve, reject) => {
            this.httpClient
                .get(`${baseUrl}/${url}`, {
                    headers: this.headers,
                    params: params,
                    observe: 'body',
                })
                .pipe(first())
                .subscribe(
                    (res) => {
                        resolve(res);
                    },
                    (err) => {
                        if (!this.isConnected) {
                            this.messageService.add({ key: 't2', severity: 'warn', summary: 'Warning', detail: 'Please check your Internet connection', life: 4000 });
                        }
                        reject(err);
                    }
                );
        });
        return promise;
    }
    post(url: string, body = {}, params = {}, baseUrl?: string, responseType: string = 'json') {
        let promise;
        promise = new Promise((resolve, reject) => {
            this.httpClient
                .post(`${this.resolveBaseUrl(url, baseUrl)}/${url}`, body, {
                    headers: this.headers,
                    params: params,
                    observe: 'body',
                    responseType: responseType as any,
                })
                .pipe(first())
                .subscribe(
                    (res) => {
                        resolve(res);
                    },
                    (err) => {
                        if (!this.isConnected) {
                            this.messageService.add({ key: 't2', severity: 'warn', summary: 'Warning', detail: 'Please check your Internet connection', life: 4000 });
                        }
                        this.error = err;
                        // No toast here: ErrorInterceptor already reports the failure with the
                        // server's reason. This one had an empty detail and only rendered on
                        // pages that happen to declare a key="t1" toast.
                        reject(err);
                    }
                );
        });
        return promise;
    }

    postForm(url: string, body: FormData, params = {}) {
        let promise;
        promise = new Promise((resolve, reject) => {
            this.httpClient
                .post(`${this.resolveBaseUrl(url)}/${url}`, body, {
                    params: params,
                    observe: 'body',
                })
                .pipe(first())
                .subscribe(
                    (res) => {
                        resolve(res);
                    },
                    (err) => {
                        if (!this.isConnected) {
                            this.messageService.add({ key: 't2', severity: 'warn', summary: 'Warning', detail: 'Please check your Internet connection', life: 4000 });
                        }
                        this.error = err;
                        reject(err);
                    }
                );
        });
        return promise;
    }

    put(url: string, body = {}, params = {}, responseType: string = 'json') {
        let promise;
        promise = new Promise((resolve, reject) => {
            this.httpClient
                .put(`${this.resolveBaseUrl(url)}/${url}`, body, {
                    headers: this.headers,
                    params: params,
                    observe: 'body',
                    responseType: responseType as any,
                })
                .pipe(first())
                .subscribe(
                    (res) => {
                        resolve(res);
                    },
                    (err) => {
                        if (!this.isConnected) {
                            this.messageService.add({ key: 't2', severity: 'warn', summary: 'Warning', detail: 'Please check your Internet connection', life: 4000 });
                        }
                        reject(err);
                    }
                );
        });
        return promise;
    }

    putwithurl(url: string, body = {}, params = {}, baseUrl: string, responseType: string = 'json') {
        let promise;
        promise = new Promise((resolve, reject) => {
            this.httpClient
                .put(`${this.resolveBaseUrl(url, baseUrl)}/${url}`, body, {
                    headers: this.headers,
                    params: params,
                    observe: 'body',
                    responseType: responseType as any,
                })
                .pipe(first())
                .subscribe(
                    (res) => {
                        resolve(res);
                    },
                    (err) => {
                        if (!this.isConnected) {
                            this.messageService.add({ key: 't2', severity: 'warn', summary: 'Warning', detail: 'Please check your Internet connection', life: 4000 });
                        }
                        reject(err);
                    }
                );
        });
        return promise;
    }

    delete(url: string, params = {}) {
        let promise;
        promise = new Promise((resolve, reject) => {
            this.httpClient
                .delete(`${this.resolveBaseUrl(url)}/${url}`, {
                    headers: this.headers,
                    params: params,
                    observe: 'body',
                })
                .pipe(first())
                .subscribe(
                    (res) => {
                        resolve(res);
                    },
                    (err) => {
                        if (!this.isConnected) {
                            this.messageService.add({ key: 't2', severity: 'warn', summary: 'Warning', detail: 'Please check your Internet connection', life: 4000 });
                        }
                        reject(err);
                    }
                );
        });
        return promise;
    }

    deletewithurl(url: string, params = {}, baseUrl: string, responseType: string = 'json') {
        let promise;
        promise = new Promise((resolve, reject) => {
            this.httpClient
                .delete(`${this.resolveBaseUrl(url, baseUrl)}/${url}`, {
                    headers: this.headers,
                    params: params,
                    observe: 'body',
                    responseType: responseType as any,
                })
                .pipe(first())
                .subscribe(
                    (res) => {
                        resolve(res);
                    },
                    (err) => {
                        if (!this.isConnected) {
                            this.messageService.add({ key: 't2', severity: 'warn', summary: 'Warning', detail: 'Please check your Internet connection', life: 4000 });
                        }
                        reject(err);
                    }
                );
        });
        return promise;
    }

    getFile(url: string, params = {}) {
        return this.httpClient.get(`${this.resolveBaseUrl(url)}/${url}`, {
            params: params,
            responseType: 'blob',
        });
    }

    postFile(url: string, body: any, params = {}) {
        return this.httpClient.post(`${this.resolveBaseUrl(url)}/${url}`, body, {
            params: params,
            responseType: 'blob',
        });
    }

    getMarineTrafficData() {
        // const headers = new HttpHeaders({
        //     // 'Content-Type': 'application/json',
        // });
        return this.httpClient
            .get(
                'https://services.marinetraffic.com/en/api/exportroutes/df844cf6b5c7973041e21bebdb2b17a6c839d933/port_start_id:1/port_target_id:1253/includealternatives:1/includeinland:1/msgtype:simple/',
                {
                    // headers: headers,
                    observe: 'response',
                }
            )
            .toPromise();
    }
}
